import React, { useState, useEffect, useRef } from 'react';
import api from '../services/api';
import STATIC_INGREDIENTS from '../data/ingredients.json';
import './IngredientInput.css';

const _extra = new Set();
let _sortedCache = null;

function getAllIngredients() {
  if (_sortedCache) return _sortedCache;
  const combined = _extra.size > 0
    ? [...new Set([...STATIC_INGREDIENTS, ..._extra])]
    : STATIC_INGREDIENTS;
  _sortedCache = [...combined].sort((a, b) => a.toLowerCase() < b.toLowerCase() ? -1 : 1);
  return _sortedCache;
}

export function addLearnedIngredient(name) {
  if (!_extra.has(name)) {
    _extra.add(name);
    _sortedCache = null;
  }
}

export default function IngredientInput({ value, onChange, placeholder }) {
  const [suggestions, setSuggestions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [correction, setCorrection] = useState(null);
  const [accepted, setAccepted] = useState(false);
  const [validating, setValidating] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const blurTimer = useRef(null);
  const debounceTimer = useRef(null);

  useEffect(() => {
    return () => {
      clearTimeout(blurTimer.current);
      clearTimeout(debounceTimer.current);
    };
  }, []);

  async function runValidation(trimmed) {
    const all = getAllIngredients();
    const known = all.some(i => i.toLowerCase() === trimmed.toLowerCase());
    if (known) { setValidating(false); return; }
    try {
      const res = await api.validateIngredient(trimmed);
      if (res.status === 'correction') {
        setCorrection(res.correction);
      } else if (res.status === 'ok') {
        addLearnedIngredient(res.name);
        setAccepted(true);
        setTimeout(() => setAccepted(false), 2500);
      }
    } catch {
      // fail-open
    } finally {
      setValidating(false);
    }
  }

  function handleChange(e) {
    const val = e.target.value;
    onChange(val);
    setCorrection(null);
    setAccepted(false);
    setActiveIdx(-1);
    clearTimeout(debounceTimer.current);

    if (val.length >= 2) {
      const all = getAllIngredients();
      const filtered = all
        .filter(i => i.toLowerCase().includes(val.toLowerCase()))
        .slice(0, 8);
      setSuggestions(filtered);

      const trimmed = val.trim();
      const exactMatch = filtered.some(i => i.toLowerCase() === trimmed.toLowerCase());
      if (trimmed.length >= 3 && !exactMatch) {
        // Sofort Indikator zeigen, API-Call nach 600ms
        setValidating(true);
        setShowDropdown(true);
        debounceTimer.current = setTimeout(() => runValidation(trimmed), 600);
      } else {
        setValidating(false);
        setShowDropdown(filtered.length > 0);
      }
    } else {
      setSuggestions([]);
      setShowDropdown(false);
      setValidating(false);
    }
  }

  function handleSelect(name) {
    clearTimeout(debounceTimer.current);
    setValidating(false);
    onChange(name);
    setSuggestions([]);
    setShowDropdown(false);
    setCorrection(null);
    setActiveIdx(-1);
  }

  function handleBlur() {
    blurTimer.current = setTimeout(() => {
      setShowDropdown(false);
    }, 150);
  }

  function handleFocus() {
    clearTimeout(blurTimer.current);
    if (value?.length >= 2 && (suggestions.length > 0 || validating)) {
      setShowDropdown(true);
    }
  }

  function handleKeyDown(e) {
    if (!showDropdown) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx(i => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx(i => Math.max(i - 1, -1));
    } else if (e.key === 'Enter' && activeIdx >= 0) {
      e.preventDefault();
      handleSelect(suggestions[activeIdx]);
    } else if (e.key === 'Escape') {
      setShowDropdown(false);
    }
  }

  function acceptCorrection() {
    onChange(correction);
    setCorrection(null);
    api.validateIngredient(correction).catch(() => {});
  }

  const dropdownOpen = showDropdown && suggestions.length > 0;

  return (
    <div className="ing-input-wrap">
      <input
        ref={inputRef}
        type="text"
        className={'field-input input-flex' + (validating ? ' ing-input-checking' : '')}
        placeholder={placeholder || 'Zutat suchen …'}
        value={value}
        onChange={handleChange}
        onBlur={handleBlur}
        onFocus={handleFocus}
        onKeyDown={handleKeyDown}
        autoComplete="off"
      />
      {validating && <span className="ing-spinner-inline"></span>}
      {accepted && <span className="ing-accepted">✓</span>}

      {dropdownOpen && (
        <ul className="ing-dropdown" ref={dropdownRef}>
          {suggestions.map((s, i) => (
            <li
              key={s}
              className={'ing-option' + (i === activeIdx ? ' active' : '')}
              onMouseDown={() => handleSelect(s)}
            >
              {s}
            </li>
          ))}
        </ul>
      )}

      {correction && (
        <div className="ing-correction">
          Meintest du: <strong>{correction}</strong>?
          <button type="button" className="ing-correction-btn" onMouseDown={acceptCorrection}>
            Übernehmen
          </button>
          <button type="button" className="ing-correction-dismiss" onMouseDown={() => setCorrection(null)}>
            ✕
          </button>
        </div>
      )}
    </div>
  );
}
